import type { ReactElement } from 'react';
import type { Filter, FiltersGroupId } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { filterTypeOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';
import { QUICK_FILTER_CATALOGUE } from '@/app/common/filters/quickFilters/quickFilterCatalogue.ts';
import {
  getMealPlanPillIcon,
  getQuickFilterIcon,
  ICON_HEART_BUBBLE,
  ICON_STAR,
} from '@/app/common/filters/surfaces/bar/resultsPills/pillIcons.tsx';

const valueKey = (filterName: string, value: string) => `${filterName}:${value}`;

/** Every quick filter value carries the icon of its own pill, keyed per filter so codes cannot collide. */
const quickFilterValueIcons: Record<string, ReactElement> = Object.fromEntries(
  Object.values(QUICK_FILTER_CATALOGUE).flatMap((quickFilter) => {
    const icon = getQuickFilterIcon(quickFilter.id);

    if (!icon) return [];

    return quickFilter.targets.flatMap((target) =>
      target.values.map((value) => [valueKey(target.filterName, value), icon] as const)
    );
  })
);

/**
 * Groups whose icon says what is being filtered rather than which value won, and so wins over
 * the per-value map: any star rating reads as a star, not only the 4-to-5 the quick filter offers.
 */
const groupIcons: Partial<Record<string, ReactElement>> = {
  [FILTER_NAME.STARS]: ICON_STAR,
  [FILTER_NAME.RATING]: ICON_HEART_BUBBLE,
};

// Meal plans have their own icon per value, quick filter or not.
const iconForValue = (filterName: string, value: string): ReactElement | undefined =>
  filterName === FILTER_NAME.MEAL_PLAN
    ? getMealPlanPillIcon(value)
    : quickFilterValueIcons[valueKey(filterName, value)];

/**
 * The icon a group pill wears once it is selected and labelled with a value, taken from the same
 * leading selection the label is built from. Undefined while nothing is selected, for filter types
 * that have no options to lead with, and for values the design has no glyph for.
 */
export const getSelectedPillIcon = (
  id: FiltersGroupId,
  filters: Filter[],
  selectionOrder?: string[]
): ReactElement | undefined => {
  const leading = filterTypeOf(filters[0]).leadingSelection?.(filters, selectionOrder);
  if (!leading) return undefined;

  return groupIcons[id] ?? iconForValue(leading.filter.name as string, leading.option.value);
};
